import {
  Check,
  Factory,
  Cog,
  Package,
  Truck,
  Shield,
  Leaf,
  PenTool,
  Ruler,
  ClipboardCheck,
  Users,
} from "lucide-react";
import { Button } from "../components/ui/button";

const processes = [
  {
    icon: Factory,
    title: "Raw Material Sourcing",
    description:
      "Premium-grade jute and cotton sourced from certified, trusted suppliers.",
  },
  {
    icon: PenTool,
    title: "Design & Precision Cutting",
    description:
      "Technical design and advanced cutting ensure accuracy and consistency.",
  },
  {
    icon: Package,
    title: "Stitching & Assembly",
    description:
      "Skilled artisans deliver strong stitching, neat finishing, and durability.",
  },
  {
    icon: Shield,
    title: "Multi-Stage Quality Control",
    description:
      "Thorough inspection at multiple checkpoints for export-grade quality.",
  },
  {
    icon: Truck,
    title: "Packaging & Global Shipping",
    description:
      "Export-grade packaging and global logistics for timely, safe delivery.",
  },
];

const capabilities = [
  {
    title: "1M+ Bags Monthly Capacity",
    description:
      "High-volume production capability to support large global programs.",
    icon: Factory,
  },
  {
    title: "Modern Manufacturing Facility",
    description:
      "Advanced machinery and organized lines designed for efficiency.",
    icon: Cog,
  },
  {
    title: "In-house Printing & Customization",
    description:
      "Screen, transfer, and digital printing options tailored to your brand.",
    icon: PenTool,
  },
  {
    title: "Automated Cutting & Stitching",
    description:
      "Automation where it matters, backed by experienced operators.",
    icon: Ruler,
  },
  {
    title: "Quality Assurance Team",
    description:
      "Dedicated QA professionals monitoring each batch and process stage.",
    icon: ClipboardCheck,
  },
  {
    title: "Design & Development Team",
    description:
      "Specialists who help translate your ideas into production-ready products.",
    icon: Users,
  },
];

export const ManufacturingPage = ({ onOpenCatalogModal }) => {
  return (
    <div className="bg-cream">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-forest/5 via-cream to-cream">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-2xl mx-auto space-y-6 text-center">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-sand">
              Manufacturing Excellence
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-forest leading-tight">
              Manufacturing <span className="text-sand">Excellence</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              At Reliable Exports, our advanced manufacturing infrastructure blends
              traditional craftsmanship with modern production technology to create
              premium-quality eco-friendly bags that meet international export
              standards. We focus on precision, efficiency, and sustainability at
              every stage of production.
            </p>
            <Button
              onClick={onOpenCatalogModal}
              className="bg-sand hover:bg-sand-dark text-white font-medium rounded-full px-8 py-6 transition-all duration-300"
            >
              Download Catalog
            </Button>
          </div>
        </div>
      </section>

      {/* Manufacturing Facility Section */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-5">
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest">
                Our Manufacturing Facility
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Our state-of-the-art production unit is equipped with modern
                machinery, automated systems, and skilled artisans who work
                together to deliver high-volume output without compromising
                craftsmanship. Every line is designed for smooth material flow,
                minimal wastage, and consistent quality.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                With a strong emphasis on quality control and ethical practices, we
                maintain a clean, organized, and highly efficient manufacturing
                environment that aligns with global compliance standards.
              </p>
              <ul className="space-y-3">
                {[
                  "Dedicated jute and cotton production lines.",
                  "Clear zoning for cutting, stitching, printing, and finishing.",
                  "Environmental and safety checks built into daily routines.",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-forest" />
                    </div>
                    <span className="text-sm md:text-base text-muted-foreground">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-card border border-sand/20 bg-cream-dark">
                <img
                  src={process.env.PUBLIC_URL + "/jute3.jpeg"}
                  alt="Reliable Exports manufacturing facility"
                  className="w-full h-[300px] md:h-[360px] lg:h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Process */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest text-center mb-4">
            Our Manufacturing Process
          </h2>
          <p className="text-sm md:text-base text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            We follow a structured, export-ready workflow that ensures every bag is
            produced with care, consistency, and accountability.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
            {processes.map((process, index) => (
              <div
                key={index}
                className="relative bg-white rounded-2xl p-6 border border-sand/20 shadow-card text-center hover:-translate-y-1 hover:shadow-hover transition-all duration-300"
              >
                <div className="flex flex-col items-center gap-3 mb-3">
                  <div className="relative">
                    <div className="w-14 h-14 bg-forest/10 rounded-full flex items-center justify-center mx-auto">
                      <process.icon className="w-7 h-7 text-forest" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 bg-sand text-white text-xs font-semibold rounded-full flex items-center justify-center">
                      {index + 1}
                    </span>
                  </div>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-forest">
                    {process.title}
                  </h3>
                </div>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  {process.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Production Capabilities */}
      <section className="py-16 md:py-24 bg-cream-dark">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest text-center mb-4">
            Our Production Capabilities
          </h2>
          <p className="text-sm md:text-base text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            Built to support fast-growing brands and large retail programs with
            reliable, scalable manufacturing.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 border border-sand/30 shadow-card flex flex-col gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center">
                  <capability.icon className="w-5 h-5 text-forest" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-forest">
                  {capability.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Production Line Section */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="bg-white rounded-3xl shadow-card border border-sand/30 overflow-hidden">
            <img
              src={process.env.PUBLIC_URL + "/manufacturing2.jpeg"}
              alt="Reliable Exports production line"
              className="w-full h-[260px] md:h-[340px] lg:h-[380px] object-cover"
            />
            <div className="px-6 md:px-10 lg:px-12 py-8 md:py-10">
              <p className="text-muted-foreground leading-relaxed max-w-3xl mx-auto text-center">
                Our streamlined production lines are engineered to handle bulk
                orders efficiently while maintaining consistency, precision, and
                premium finishing standards—ensuring every shipment reflects the
                reliability of your brand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OEM & Private Label Services */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="bg-white rounded-3xl p-8 md:p-12 text-forest shadow-card border border-sand/30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div className="space-y-4">
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest">
                  OEM & Private Label Solutions
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  We offer complete OEM and private label manufacturing solutions
                  that allow you to bring unique concepts to life. From design
                  consultation to final shipment, our team manages the entire
                  process seamlessly.
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold tracking-[0.22em] uppercase text-muted-foreground mb-4">
                  Our Customization Services Include
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    "Custom Bag Design Development",
                    "Brand Logo Printing & Labeling",
                    "Multiple Size Options",
                    "Accurate Color Matching",
                    "Material Selection Assistance",
                    "Custom Packaging Design",
                  ].map((service, index) => (
                    <div
                      key={index}
                      className="bg-cream-dark rounded-xl px-4 py-3 text-sm font-medium text-forest"
                    >
                      {service}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-20 bg-forest">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
            Start Your Manufacturing Partnership Today
          </h2>
          <p className="text-sm md:text-base text-cream/90 max-w-2xl mx-auto mb-8">
            Connect with our team to discuss your eco-friendly bag requirements,
            explore customization options, and plan scalable, export-ready
            manufacturing programs.
          </p>
          <Button
            onClick={onOpenCatalogModal}
            className="bg-sand hover:bg-sand-dark text-white font-medium rounded-full px-8 py-6 transition-all duration-300"
          >
            Contact Our Team
          </Button>
        </div>
      </section>
    </div>
  );
};
